import { atom } from 'nanostores';
import { db, projectId } from '~/lib/persistence/useChatHistory';
import { updateProjectRules, getRules } from '~/lib/persistence/db';
import { selectionStore } from '~/lib/persistence/selectionStore';

interface RuleState {
  projectRules: string | null; // Content of the rules file
  loaded: boolean; // Whether rules have been loaded from the database
  source: 'chat' | 'temporary' | null; // Track where rules came from
}

export const rulesStore = atom<RuleState>({
  projectRules: null,
  loaded: false,
  source: null,
});

export async function setProjectRules(rules: string): Promise<boolean> {
  const currentProjectId = projectId.get();
  const isTemporary = !currentProjectId;

  // Update store state immediately for responsive UI
  rulesStore.set({
    projectRules: rules,
    loaded: true,
    source: isTemporary ? 'temporary' : 'chat',
  });

  // Store in localStorage for temporary rules, or IndexedDB for persistent rules
  if (isTemporary) {
    console.log('[useChatHistory] Saving temporary rules to localStorage');
    selectionStore.setKey('temporaryProjectRules', rules);
    return true;
  }

  // Otherwise, store in IndexedDB if we have a database and chat ID
  if (db && currentProjectId) {
    try {
      console.log('[useChatHistory] Saving rules to IndexedDB and cloud for project:', currentProjectId);

      const success = await updateProjectRules(db, currentProjectId, rules);
      let isCloudSuccess = false;

      console.log('[useChatHistory] Saving rules to Cloud for project:', currentProjectId);

      const res = await fetch('/api/project-rules', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: currentProjectId as string, projectRules: rules }),
      });

      if (res.ok) {
        console.log('[useChatHistory] Successfully saved rules to Cloud');
        isCloudSuccess = true;
      } else {
        console.warn('[useChatHistory] Failed to save rules to Cloud');
        return false;
      }

      if (success && isCloudSuccess) {
        console.log('[useChatHistory] Successfully saved rules to IndexedDB');

        // Clear any temporary rules if successful
        if (selectionStore.get().temporaryProjectRules) {
          selectionStore.setKey('temporaryProjectRules', '');
        }

        return true;
      }

      console.warn('[useChatHistory] Failed to save rules to IndexedDB or cloud');
      return false;
    } catch (error) {
      /*
       * Sentry.captureException(error, {
       *   extra: { message: 'Error saving rules to IndexedDB', chatId: currentChatId, rules },
       * });
       */
      console.error('[useChatHistory] Error saving rules to IndexedDB or cloud:', error);
      return false;
    }
  }

  return false;
}

export async function loadChatSpecificRules(): Promise<void> {
  // Set initial loading state
  rulesStore.set({
    projectRules: null,
    loaded: false,
    source: null,
  });

  const currentProjectId = projectId.get();
  const tempRules = selectionStore.get().temporaryProjectRules;

  console.log('[useChatHistory] Loading rules - chatId:', currentProjectId, 'tempRules exist:', !!tempRules);

  try {
    // Handle no database case first (simplifies logic)
    if (!db) {
      console.log('[useChatHistory] No database available');

      // If we have temporary rules and no chatId, use them
      if (tempRules && !currentProjectId) {
        rulesStore.set({
          projectRules: tempRules,
          loaded: true,
          source: 'temporary',
        });
        return;
      }

      // Otherwise, empty rules
      rulesStore.set({
        projectRules: null,
        loaded: true,
        source: null,
      });
      return;
    }

    // CASE: No chat ID - use temporary rules or empty
    if (!currentProjectId) {
      rulesStore.set({
        projectRules: tempRules || null,
        loaded: true,
        source: tempRules ? 'temporary' : null,
      });

      console.log(
        tempRules
          ? '[rules] Using temporary rules from localStorage'
          : '[rules] No chat ID and no temporary rules, initializing empty',
      );

      return;
    }

    // CASE: Chat ID exists (local IndexedDB persistence)
    if (currentProjectId) {
      // If we have temporary rules, migrate them
      if (tempRules) {
        console.log('[rules] Migrating temporary rules to chat:', currentProjectId, tempRules);

        // migrating rules to idb
        const success = await updateProjectRules(db, currentProjectId, tempRules);
        let isCloudSuccess = false;

        // migrating rules to cloud
        try {
          const res = await fetch(`/api/project-rules`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ projectId: currentProjectId, projectRules: tempRules }),
          });

          if (res.ok) {
            console.log('[rules] Successfully migrated temporary rules to Cloud');
            isCloudSuccess = true;
          }
        } catch (error) {
          console.error('[rules] Migration to Cloud failed, will try to load existing rules:', error);
        }

        // ensure both migrations were successful
        if (success && isCloudSuccess) {
          console.log('[rules] Successfully migrated temporary rules');
          selectionStore.setKey('temporaryProjectRules', '');

          rulesStore.set({
            projectRules: tempRules,
            loaded: true,
            source: 'chat',
          });
          return;
        }

        console.log('[rules] Migration failed, loading existing rules');
      }
    }

    // Load existing rules from database (only if we have both chatId and projectId)
    if (!currentProjectId) {
      console.log('[rules] Missing chatId or projectId, cannot load rules from database');
      rulesStore.set({
        projectRules: null,
        loaded: true,
        source: null,
      });
      return;
    }

    console.log('[rules] Loading existing rules for chat:', currentProjectId);

    const rules = await getRules(db, currentProjectId);

    rulesStore.set({
      projectRules: rules,
      loaded: true,
      source: rules ? 'chat' : null,
    });

    console.log(rules ? '[rules] Loaded rules successfully' : '[rules] No rules found for this chat');
  } catch (error) {
    /*
     * Sentry.captureException(error, {
     *   extra: { message: 'Error loading rules', chatId: currentChatId },
     *   level: 'log',
     * });
     */
    console.error('[rules] Error loading rules:', error);

    rulesStore.set({
      projectRules: null,
      loaded: true,
      source: null,
    });
  }
}

export function clearProjectRules(): void {
  console.log('[rules] Clearing all project rules');

  rulesStore.set({
    projectRules: null,
    loaded: true,
    source: null,
  });

  if (selectionStore.get().temporaryProjectRules) {
    selectionStore.setKey('temporaryProjectRules', '');
  }
}
